import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Card, Form, Spinner } from 'react-bootstrap';
import { GET_SUBSCRIPTION_FEATURE_TOGGLES, UPDATE_SUBSCRIPTION_FEATURE_TOGGLES } from './graphql';

type FullRefundToggle = {
  purchaseType: string;
  enabled: boolean;
};

type SubscriptionFeatureToggleValues = {
  autoRenewReminderPopupEnabled: boolean;
  sandboxSubscriptionSkuCheatEnabled: boolean;
  sandboxSubscriptionSkuCheatUserIds: string[];
  economicTransactionReceiptLogEnabled: boolean;
  unblockFriendEnabled: boolean;
  fullRefundToggles: FullRefundToggle[];
};

// What a store refund of each purchase type takes back when its toggle is ON. Types the backend adds later still
// show up, with their raw name.
const FULL_REFUND_LABELS: Record<string, { label: string; description: string }> = {
  CHAINED_OFFER: {
    label: 'Chained offers',
    description: 'Everything the refunded step gave, and the later steps are paused.',
  },
  SALE_BUNDLE: { label: 'Sale bundles', description: 'Everything the bundle gave.' },
  STARTER_PACK: { label: 'Starter packs', description: 'Everything the pack gave, fragments included.' },
  PIGGY_BANK: { label: 'Piggy banks', description: 'Everything the piggy bank gave.' },
  MAESTRO_PACK: { label: 'Songpop Maestro', description: 'The Maestro pass and its instant rewards.' },
  IAP_GIFT: {
    label: 'Gifts to friends',
    description: 'Not claimed yet: the gift is cancelled. Claimed: what it gave is taken back from the recipient.',
  },
  SUBSCRIPTION_DAYS: { label: 'Subscription days', description: 'The purchased subscription days.' },
  PAID_CALENDAR: { label: 'Paid calendars', description: 'The calendar and the days already claimed.' },
  LOYALTY_POINTS: {
    label: 'Fame (loyalty points)',
    description: 'The Fame the purchase awarded, whatever the purchase type. Cards already claimed with it are kept.',
  },
};

type SubscriptionFeatureTogglesQuery = {
  app: {
    id: string;
  } & SubscriptionFeatureToggleValues;
};

type UpdateSubscriptionFeatureTogglesMutation = {
  updateAppFeatureToggles: SubscriptionFeatureToggleValues;
};

type UpdateSubscriptionFeatureTogglesVariables = Partial<SubscriptionFeatureToggleValues>;

const SubscriptionFeatureToggles = () => {
  const { data, loading, error } = useQuery<SubscriptionFeatureTogglesQuery>(GET_SUBSCRIPTION_FEATURE_TOGGLES, {
    fetchPolicy: 'network-only',
  });
  const [updateToggles, { loading: updateLoading }] = useMutation<
    UpdateSubscriptionFeatureTogglesMutation,
    UpdateSubscriptionFeatureTogglesVariables
  >(UPDATE_SUBSCRIPTION_FEATURE_TOGGLES, {
    refetchQueries: [{ query: GET_SUBSCRIPTION_FEATURE_TOGGLES }],
  });

  const toggles = data?.app;
  const isSaving = updateLoading;
  const [autoRenewReminderPopupEnabled, setAutoRenewReminderPopupEnabled] = useState(false);
  const [sandboxSubscriptionSkuCheatEnabled, setSandboxSubscriptionSkuCheatEnabled] = useState(false);
  const [economicTransactionReceiptLogEnabled, setEconomicTransactionReceiptLogEnabled] = useState(true);
  const [unblockFriendEnabled, setUnblockFriendEnabled] = useState(false);
  const [fullRefundToggles, setFullRefundToggles] = useState<FullRefundToggle[]>([]);
  const [sandboxUserIdsText, setSandboxUserIdsText] = useState('');
  const [showSandboxUserIdsError, setShowSandboxUserIdsError] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const sandboxUserIds = useMemo(
    () =>
      sandboxUserIdsText
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter(Boolean),
    [sandboxUserIdsText]
  );

  useEffect(() => {
    setAutoRenewReminderPopupEnabled(!!toggles?.autoRenewReminderPopupEnabled);
    setSandboxSubscriptionSkuCheatEnabled(!!toggles?.sandboxSubscriptionSkuCheatEnabled);
    setEconomicTransactionReceiptLogEnabled(toggles?.economicTransactionReceiptLogEnabled ?? true);
    setUnblockFriendEnabled(!!toggles?.unblockFriendEnabled);
    setFullRefundToggles(
      (toggles?.fullRefundToggles ?? []).map(({ purchaseType, enabled }) => ({ purchaseType, enabled }))
    );
    setSandboxUserIdsText(toggles?.sandboxSubscriptionSkuCheatUserIds.join('\n') ?? '');
  }, [toggles]);

  const handleSave = () => {
    if (sandboxSubscriptionSkuCheatEnabled && sandboxUserIds.length === 0) {
      setShowSandboxUserIdsError(true);
      return;
    }

    setShowSandboxUserIdsError(false);
    updateToggles({
      variables: {
        autoRenewReminderPopupEnabled,
        sandboxSubscriptionSkuCheatEnabled,
        sandboxSubscriptionSkuCheatUserIds: sandboxUserIds,
        economicTransactionReceiptLogEnabled,
        unblockFriendEnabled,
        fullRefundToggles,
      },
    })
      .then(() => {
        setShowSaveSuccess(true);
      })
      .catch(() => {
        alert('Something went wrong :(');
      });
  };

  return (
    <div className="d-flex flex-column">
      <h1>Feature Toggles</h1>
      <p className="text-muted">
        Backend-managed switches, grouped by what they affect. Changes apply to this environment only and take effect
        within a minute, no deploy needed.
      </p>

      {error ? <Alert variant="danger">{error.message}</Alert> : null}
      {showSaveSuccess ? <Alert variant="success">successfully saved</Alert> : null}

      <Card className="mb-3">
        <Card.Body>
          {loading && !toggles ? (
            <Spinner animation="border" />
          ) : (
            <>
              <h5 className="mb-1">Subscriptions</h5>
              <p className="text-muted small mb-3">Client behaviour for every player.</p>
              <Form.Group controlId="autoRenewReminderPopupEnabled">
                <Form.Check
                  type="switch"
                  label="Auto renewal subscription popup"
                  checked={autoRenewReminderPopupEnabled}
                  disabled={isSaving}
                  onChange={(event) => {
                    setAutoRenewReminderPopupEnabled(event.currentTarget.checked);
                    setShowSaveSuccess(false);
                  }}
                />
                <Form.Text className="text-muted">Shows the renewal reminder popup.</Form.Text>
              </Form.Group>

              <hr />
              <h5 className="mb-1">Social</h5>
              <p className="text-muted small mb-3">Client behaviour for every player.</p>
              <Form.Group controlId="unblockFriendEnabled">
                <Form.Check
                  type="switch"
                  label="Unblock players FE"
                  checked={unblockFriendEnabled}
                  disabled={isSaving}
                  onChange={(event) => {
                    setUnblockFriendEnabled(event.currentTarget.checked);
                    setShowSaveSuccess(false);
                  }}
                />
                <Form.Text className="text-muted">
                  ON: the Friends screen has Friends / Blocked tabs, players can unblock from the Blocked tab, and
                  blocking someone opens the Blocked tab. OFF: the old Friends screen without tabs, blocking goes back
                  to the main menu, and the backend rejects player unblocks (admin Unblock still works). Default: OFF.
                  Players see the change the next time they open the Friends screen.
                </Form.Text>
              </Form.Group>

              <hr />
              <h5 className="mb-1">Refunds</h5>
              <p className="text-muted small mb-3">
                What a Google / Apple refund takes back from the player. ON: everything the purchase gave (never below
                0, items the player no longer has are left). OFF: only the diamonds, like before. Purchases made before
                the full refund was released fall back to diamonds only. Default: OFF.
              </p>
              {fullRefundToggles.map(({ purchaseType, enabled }) => (
                <Form.Group key={purchaseType} controlId={`fullRefund-${purchaseType}`}>
                  <Form.Check
                    type="switch"
                    label={FULL_REFUND_LABELS[purchaseType]?.label ?? purchaseType}
                    checked={enabled}
                    disabled={isSaving}
                    onChange={(event) => {
                      const { checked } = event.currentTarget;
                      setFullRefundToggles((current) =>
                        current.map((toggle) =>
                          toggle.purchaseType === purchaseType ? { ...toggle, enabled: checked } : toggle
                        )
                      );
                      setShowSaveSuccess(false);
                    }}
                  />
                  <Form.Text className="text-muted">
                    {FULL_REFUND_LABELS[purchaseType]?.description} <code>{purchaseType}_FULL_REFUND</code>
                  </Form.Text>
                </Form.Group>
              ))}

              <hr />
              <h5 className="mb-1">QA / Sandbox</h5>
              <p className="text-muted small mb-3">Client test tooling, visible only to the allowed user ids below.</p>
              <Form.Group controlId="sandboxSubscriptionSkuCheat" className="mb-0">
                <Form.Check
                  type="switch"
                  label="Sandbox subscription SKU buttons"
                  checked={sandboxSubscriptionSkuCheatEnabled}
                  disabled={isSaving}
                  onChange={(event) => {
                    setSandboxSubscriptionSkuCheatEnabled(event.currentTarget.checked);
                    setShowSaveSuccess(false);
                    if (!event.currentTarget.checked) {
                      setShowSandboxUserIdsError(false);
                    }
                  }}
                />
                <Form.Text className="text-muted">Shows SKU test buttons for allowed users.</Form.Text>
              </Form.Group>

              <Form.Group controlId="sandboxSubscriptionSkuCheatUserIds" className="mt-3 mb-0">
                <Form.Label>Allowed user ids</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  isInvalid={showSandboxUserIdsError}
                  value={sandboxUserIdsText}
                  disabled={isSaving}
                  onChange={(event) => {
                    setSandboxUserIdsText(event.currentTarget.value);
                    setShowSaveSuccess(false);
                    if (showSandboxUserIdsError) {
                      setShowSandboxUserIdsError(false);
                    }
                  }}
                />
                <Form.Control.Feedback type="invalid">Add at least one user id.</Form.Control.Feedback>
                <Form.Text className="text-muted">Separate ids with commas, spaces, or new lines.</Form.Text>
              </Form.Group>

              <hr />
              <h5 className="mb-1">Server logging</h5>
              <p className="text-muted small mb-3">
                Backend only, no effect on players. Default differs per environment: ON on test, OFF on beta and prod.
              </p>
              <Form.Group controlId="economicTransactionReceiptLogEnabled">
                <Form.Check
                  type="switch"
                  label="Server receipt logs for store purchases"
                  checked={economicTransactionReceiptLogEnabled}
                  disabled={isSaving}
                  onChange={(event) => {
                    setEconomicTransactionReceiptLogEnabled(event.currentTarget.checked);
                    setShowSaveSuccess(false);
                  }}
                />
                <Form.Text className="text-muted">
                  Writes a <code>[economic_transaction] store purchase receipt</code> line (GRANTED / DUPLICATE_SKIPPED,
                  with userId) to Cloud Logging for every store purchase, to reconcile against the client&apos;s
                  economic_transaction events. Default: ON on test, OFF on beta and prod. Saving here overrides the
                  default for this environment. Applies within a minute.
                </Form.Text>
              </Form.Group>

              <button type="button" className="btn btn-primary mt-3" disabled={isSaving} onClick={handleSave}>
                Save
              </button>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SubscriptionFeatureToggles;
